import { useEffect, useState } from 'react';
import { convertAPIAuthTokenResponseToNormalShape, getJwtLanguageFromAccessToken } from '../utils/auth.ts';
import { b2cVersion } from '../utils/consts.ts';
import {
  getLocalStorageValue,
  removeLocalStorageValue,
  setLocalStorageValue,
} from '../utils/local-storage.ts';
import { requestIframeStorageAccess } from '../utils/iframe-storage.ts';
import { getUserTokenBySessionId } from '../services/apis/userAuthAPI.ts';
import { useAuthStore } from '../services/store';
import { applyLanguageFromApi, getLanguageFromUrl } from '../utils/i18n.ts';

const getSessionIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    sessionId: urlParams.get('sessionid'),
    mode: urlParams.get('mode') || 'REAL',
  };
};

const stripSessionIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete('sessionid');
  const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
  window.history.replaceState({}, '', newUrl);
};

const hasSessionIdInUrl = () => {
  if (b2cVersion) {
    return false;
  }

  const { sessionId, mode } = getSessionIdFromUrl();
  return Boolean(sessionId && mode);
};

export const useSessionIdHandler = () => {
  const { checkAuthStatus } = useAuthStore();
  const [hasSessionIdParam, setHasSessionIdParam] = useState(hasSessionIdInUrl);
  const [isHandlingSessionId, setIsHandlingSessionId] = useState(hasSessionIdInUrl);

  useEffect(() => {
    if (b2cVersion) {
      return;
    }

    const { sessionId, mode } = getSessionIdFromUrl();

    if (!sessionId || !mode) {
      setHasSessionIdParam(false);
      setIsHandlingSessionId(false);
      return;
    }

    let cancelled = false;

    setHasSessionIdParam(true);
    setIsHandlingSessionId(true);

    // Clear any stale token before exchanging a new sessionid (e.g. after days away).
    removeLocalStorageValue('userAccessToken');
    checkAuthStatus();

    (async () => {
      await requestIframeStorageAccess();

      if (cancelled) {
        return;
      }

      try {
        const res = await getUserTokenBySessionId(sessionId, mode);

        if (cancelled) {
          return;
        }

        console.log('getUserTokenBySessionId res', res);

        if (!res.data.access_token) {
          return;
        }

        const token = convertAPIAuthTokenResponseToNormalShape(res.data);
        setLocalStorageValue('userAccessToken', token);
        if (!getLanguageFromUrl()) {
          applyLanguageFromApi(getJwtLanguageFromAccessToken(token.accessToken));
        }

        const storedToken = getLocalStorageValue('userAccessToken')?.accessToken;
        const isAuthenticated = checkAuthStatus();

        if (storedToken && isAuthenticated) {
          stripSessionIdFromUrl();
          setHasSessionIdParam(false);
          return;
        }

        console.warn(
          '[coinraces] Auth token received but could not be read back from storage; keeping sessionid in URL for retry'
        );
      } catch (error) {
        console.error('Failed to get token by sessionid', error);
      } finally {
        if (!cancelled) {
          checkAuthStatus();
          setIsHandlingSessionId(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkAuthStatus]);

  return { hasSessionIdParam, isHandlingSessionId };
};
